import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Sparkles, UploadCloud, FileText, Send, RefreshCw, X, Crown, AlertCircle, Bot, User } from 'lucide-react';
import { api, formatApiErrorDetail, formatBytes } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

function QuotaBadge() {
  const [quota, setQuota] = React.useState(null);
  const fetchQuota = React.useCallback(() => {
    api.get('/ai/quota').then(({ data }) => setQuota(data)).catch(() => {});
  }, []);
  React.useEffect(() => {
    fetchQuota();
    const handler = () => fetchQuota();
    window.addEventListener('valutdocu:quota-changed', handler);
    return () => window.removeEventListener('valutdocu:quota-changed', handler);
  }, [fetchQuota]);
  if (!quota) return null;
  if (quota.pro) {
    return (
      <div data-testid="quota-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-vault-gold/10 border border-vault-gold/30 text-vault-gold text-xs font-mono tracking-wider">
        <Crown className="w-3.5 h-3.5" /> PRO · UNLIMITED
      </div>
    );
  }
  return (
    <div data-testid="quota-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono tracking-wider text-gray-300">
      <Sparkles className="w-3.5 h-3.5 text-vault-gold" /> {quota.remaining}/{quota.limit} FREE AI USES TODAY
    </div>
  );
}

function notifyQuotaChanged() {
  window.dispatchEvent(new Event('valutdocu:quota-changed'));
}

export function AIToolShell({ title, subtitle, children, testId }) {
  return (
    <div data-testid={testId} className="vault-grid-bg min-h-screen">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-12 pb-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-vault-gold mb-6 transition">
          ← All tools
        </Link>
        <div className="flex flex-wrap items-start gap-3 justify-between mb-2">
          <p className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold">AI Tools · Powered by Gemini</p>
          <QuotaBadge />
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tighter">
          {title}
        </h1>
        <p className="text-gray-400 mt-3 max-w-2xl">{subtitle}</p>
        <div className="mt-10">{children}</div>
      </div>
    </div>
  );
}

function DropZone({ onFile, accept = '.pdf', testId = 'dropzone' }) {
  const onDrop = React.useCallback((files) => files[0] && onFile(files[0]), [onFile]);
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop, multiple: false, noClick: true,
  });
  return (
    <div
      {...getRootProps()}
      data-testid={testId}
      className={`dropzone rounded-2xl p-10 md:p-16 text-center cursor-pointer ${isDragActive ? 'active' : ''}`}
      onClick={open}
    >
      <input {...getInputProps()} data-testid="file-input" accept={accept} />
      <div className="flex flex-col items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-vault-gold/10 flex items-center justify-center border border-vault-gold/30 animate-pulse-glow">
          <UploadCloud className="w-9 h-9 text-vault-gold" />
        </div>
        <h3 className="text-xl md:text-2xl font-heading font-semibold text-white">
          Drag &amp; drop your PDF
        </h3>
        <p className="text-gray-400 text-sm">or click to browse</p>
        <button
          data-testid="select-file-button"
          className="btn-primary mt-2"
          onClick={(e) => { e.stopPropagation(); open(); }}
        >
          Select PDF
        </button>
      </div>
    </div>
  );
}

function QuotaUpgrade({ message }) {
  return (
    <div data-testid="quota-exceeded" className="glass-panel rounded-2xl p-8 text-center">
      <Crown className="w-12 h-12 text-vault-gold mx-auto mb-4" />
      <h3 className="text-xl font-heading font-semibold mb-2">You hit your free AI limit.</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">{message}</p>
      <Link to="/pricing" data-testid="upgrade-cta" className="btn-primary">Upgrade to Pro — ₹149/mo · 7-day free trial</Link>
    </div>
  );
}

// ============ AI SUMMARIZE ============
export function AISummarizePage() {
  const [file, setFile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState('');
  const [error, setError] = React.useState('');
  const [quotaErr, setQuotaErr] = React.useState('');

  const submit = async () => {
    setLoading(true); setError(''); setQuotaErr('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/ai/summarize', fd);
      setSummary(data.summary);
      notifyQuotaChanged();
      toast.success('Summary ready!');
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 429) setQuotaErr(detail);
      else setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  if (quotaErr) {
    return (
      <AIToolShell
        title={<>AI Summarize <span className="gold-text">PDF</span></>}
        subtitle="Instant key-point extraction from any PDF — powered by Gemini Flash."
        testId="ai-summarize-page"
      >
        <QuotaUpgrade message={quotaErr} />
      </AIToolShell>
    );
  }

  return (
    <AIToolShell
      title={<>AI Summarize <span className="gold-text">PDF</span></>}
      subtitle="Instant key-point extraction from any PDF — powered by Gemini Flash."
      testId="ai-summarize-page"
    >
      {!summary && !loading && !file && <DropZone onFile={setFile} />}
      {file && !summary && !loading && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-vault-paper/60 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-vault-gold" />
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{file.name}</p>
                <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
              </div>
            </div>
            <button data-testid="remove-file" onClick={() => setFile(null)} className="text-gray-500 hover:text-red-400"><X className="w-4 h-4" /></button>
          </div>
          <button data-testid="summarize-button" onClick={submit} className="btn-primary w-full">
            Summarize with AI →
          </button>
        </div>
      )}
      {loading && (
        <div data-testid="processing-view" className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-vault-gold/10 border border-vault-gold/30 flex items-center justify-center mx-auto mb-6">
            <Bot className="w-10 h-10 text-vault-gold animate-pulse" />
          </div>
          <h3 className="text-xl font-heading font-semibold">Gemini is reading your PDF…</h3>
          <p className="text-gray-400 text-sm mt-2 font-mono">Usually takes 5-15 seconds</p>
        </div>
      )}
      {summary && (
        <div data-testid="summary-result" className="space-y-4">
          <div className="glass-panel rounded-2xl p-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-vault-gold" />
              <span className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold">AI Summary</span>
            </div>
            <div className="prose prose-invert prose-headings:font-heading prose-headings:text-white prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-vault-gold max-w-none">
              <ReactMarkdown>{summary}</ReactMarkdown>
            </div>
          </div>
          <button data-testid="reset-button" onClick={() => { setFile(null); setSummary(''); }} className="btn-ghost inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Summarize Another
          </button>
        </div>
      )}
      {error && <p data-testid="error-message" className="text-red-400 mt-4 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</p>}
    </AIToolShell>
  );
}

// ============ AI TRANSLATE ============
export function AITranslatePage() {
  const [file, setFile] = React.useState(null);
  const [lang, setLang] = React.useState('Hindi');
  const [loading, setLoading] = React.useState(false);
  const [resultUrl, setResultUrl] = React.useState(null);
  const [error, setError] = React.useState('');
  const [quotaErr, setQuotaErr] = React.useState('');

  const languages = ['Hindi', 'English', 'French', 'Spanish', 'German', 'Arabic', 'Chinese', 'Japanese'];

  const submit = async () => {
    setLoading(true); setError(''); setQuotaErr('');
    const fd = new FormData(); fd.append('file', file); fd.append('target_language', lang);
    try {
      const { data } = await api.post('/ai/translate', fd, { responseType: 'blob' });
      setResultUrl(URL.createObjectURL(data));
      notifyQuotaChanged();
      toast.success('Translation ready!');
    } catch (e) {
      let detail = 'Translation failed';
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch {}
      } else detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 429) setQuotaErr(detail);
      else setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  if (quotaErr) return (
    <AIToolShell title={<>AI Translate <span className="gold-text">PDF</span></>} subtitle="Translate any PDF to 8+ languages." testId="ai-translate-page">
      <QuotaUpgrade message={quotaErr} />
    </AIToolShell>
  );

  return (
    <AIToolShell
      title={<>AI Translate <span className="gold-text">PDF</span></>}
      subtitle="Translate any PDF to 8+ languages while preserving structure — powered by Gemini Flash."
      testId="ai-translate-page"
    >
      {!file && <DropZone onFile={setFile} />}
      {file && !resultUrl && !loading && (
        <div className="space-y-5">
          <div className="flex items-center justify-between bg-vault-paper/60 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-vault-gold" />
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{file.name}</p>
                <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
              </div>
            </div>
            <button data-testid="remove-file" onClick={() => setFile(null)}><X className="w-4 h-4 text-gray-500" /></button>
          </div>
          <div className="bg-vault-paper/40 border border-white/10 rounded-xl p-5">
            <p className="text-sm font-mono tracking-wider uppercase text-gray-400 mb-3">Target language</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {languages.map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l.toLowerCase()}`}
                  type="button"
                  onClick={() => setLang(l)}
                  className={`p-2.5 rounded-lg border text-sm ${lang === l ? 'border-vault-gold bg-vault-gold/10 text-vault-gold' : 'border-white/10 text-gray-300 hover:border-white/30'}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="translate-button" onClick={submit} className="btn-primary w-full">Translate to {lang} →</button>
        </div>
      )}
      {loading && (
        <div data-testid="processing-view" className="text-center py-16">
          <Bot className="w-12 h-12 text-vault-gold animate-pulse mx-auto mb-4" />
          <h3 className="text-xl font-heading font-semibold">Translating to {lang}…</h3>
        </div>
      )}
      {resultUrl && (
        <div data-testid="translate-result" className="text-center py-12">
          <Sparkles className="w-12 h-12 text-vault-gold mx-auto mb-4" />
          <h3 className="text-2xl font-heading font-bold mb-6">Translation ready!</h3>
          <div className="flex gap-3 justify-center">
            <a data-testid="download-button" href={resultUrl} download={`translated_${lang}.pdf`} className="btn-primary">Download PDF</a>
            <button onClick={() => { setFile(null); setResultUrl(null); }} className="btn-ghost">Translate Another</button>
          </div>
        </div>
      )}
      {error && <p className="text-red-400 mt-4">{error}</p>}
    </AIToolShell>
  );
}

// ============ AI EXTRACT DATA ============
export function AIExtractPage() {
  const [file, setFile] = React.useState(null);
  const [type, setType] = React.useState('all');
  const [loading, setLoading] = React.useState(false);
  const [resultUrl, setResultUrl] = React.useState(null);
  const [error, setError] = React.useState('');
  const [quotaErr, setQuotaErr] = React.useState('');

  const types = [
    { v: 'all', label: 'All structured data' },
    { v: 'tables', label: 'Tables only' },
    { v: 'emails', label: 'Email addresses' },
    { v: 'dates', label: 'Dates' },
    { v: 'phones', label: 'Phone numbers' },
    { v: 'invoices', label: 'Invoice fields' },
  ];

  const submit = async () => {
    setLoading(true); setError(''); setQuotaErr('');
    const fd = new FormData(); fd.append('file', file); fd.append('extract_type', type);
    try {
      const { data } = await api.post('/ai/extract', fd, { responseType: 'blob' });
      setResultUrl(URL.createObjectURL(data));
      notifyQuotaChanged();
      toast.success('Data extracted!');
    } catch (e) {
      let detail = 'Extraction failed';
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch {}
      } else detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 429) setQuotaErr(detail);
      else setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  if (quotaErr) return (
    <AIToolShell title={<>AI Extract <span className="gold-text">Data</span></>} subtitle="Extract structured data from PDFs into Excel." testId="ai-extract-page">
      <QuotaUpgrade message={quotaErr} />
    </AIToolShell>
  );

  return (
    <AIToolShell
      title={<>AI Extract <span className="gold-text">Data</span></>}
      subtitle="Pull tables, emails, dates, invoices, and more from any PDF straight into Excel."
      testId="ai-extract-page"
    >
      {!file && <DropZone onFile={setFile} />}
      {file && !resultUrl && !loading && (
        <div className="space-y-5">
          <div className="flex items-center justify-between bg-vault-paper/60 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-vault-gold" />
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{file.name}</p>
                <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
              </div>
            </div>
            <button onClick={() => setFile(null)}><X className="w-4 h-4 text-gray-500" /></button>
          </div>
          <div className="bg-vault-paper/40 border border-white/10 rounded-xl p-5">
            <p className="text-sm font-mono tracking-wider uppercase text-gray-400 mb-3">What to extract</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {types.map((t) => (
                <button
                  key={t.v}
                  data-testid={`extract-type-${t.v}`}
                  type="button"
                  onClick={() => setType(t.v)}
                  className={`p-3 rounded-lg border text-sm text-left ${type === t.v ? 'border-vault-gold bg-vault-gold/10 text-vault-gold' : 'border-white/10 text-gray-300'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="extract-button" onClick={submit} className="btn-primary w-full">Extract Data →</button>
        </div>
      )}
      {loading && (
        <div data-testid="processing-view" className="text-center py-16">
          <Bot className="w-12 h-12 text-vault-gold animate-pulse mx-auto mb-4" />
          <h3 className="text-xl font-heading font-semibold">Extracting structured data…</h3>
        </div>
      )}
      {resultUrl && (
        <div data-testid="extract-result" className="text-center py-12">
          <Sparkles className="w-12 h-12 text-vault-gold mx-auto mb-4" />
          <h3 className="text-2xl font-heading font-bold mb-6">Data extracted to Excel!</h3>
          <div className="flex gap-3 justify-center">
            <a data-testid="download-button" href={resultUrl} download="extracted.xlsx" className="btn-primary">Download .xlsx</a>
            <button onClick={() => { setFile(null); setResultUrl(null); }} className="btn-ghost">Extract Another</button>
          </div>
        </div>
      )}
      {error && <p className="text-red-400 mt-4">{error}</p>}
    </AIToolShell>
  );
}

// ============ AI CHAT WITH PDF ============
export function AIChatPage() {
  const [file, setFile] = React.useState(null);
  const [sessionId, setSessionId] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [quotaErr, setQuotaErr] = React.useState('');
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const startSession = async (f) => {
    setFile(f); setLoading(true); setError(''); setQuotaErr('');
    const fd = new FormData(); fd.append('file', f);
    try {
      const { data } = await api.post('/ai/chat/start', fd);
      setSessionId(data.session_id);
      setMessages([{ role: 'assistant', content: `Hi! I've loaded **${data.filename}**. Ask me anything about it.` }]);
      notifyQuotaChanged();
      toast.success('PDF loaded. Ask away!');
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 429) setQuotaErr(detail);
      else setError(detail);
      toast.error(detail);
      setFile(null);
    } finally { setLoading(false); }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading || !sessionId) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post('/ai/chat/message', { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
      notifyQuotaChanged();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 429) {
        setQuotaErr(detail);
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: `⚠️ ${detail}`, error: true }]);
      }
      toast.error(detail);
    } finally { setLoading(false); }
  };

  if (quotaErr) return (
    <AIToolShell title={<>AI Chat <span className="gold-text">with PDF</span></>} subtitle="Ask any question about your PDF." testId="ai-chat-page">
      <QuotaUpgrade message={quotaErr} />
    </AIToolShell>
  );

  return (
    <AIToolShell
      title={<>AI Chat <span className="gold-text">with PDF</span></>}
      subtitle="Upload a PDF and have a natural conversation about it — powered by Gemini Flash."
      testId="ai-chat-page"
    >
      {!sessionId && !loading && <DropZone onFile={startSession} />}
      {loading && !sessionId && (
        <div className="text-center py-16">
          <Bot className="w-12 h-12 text-vault-gold animate-pulse mx-auto mb-4" />
          <p className="text-gray-400">Loading PDF into context…</p>
        </div>
      )}
      {sessionId && (
        <div className="glass-panel rounded-2xl overflow-hidden flex flex-col" style={{ height: '70vh' }}>
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-vault-gold shrink-0" />
              <span className="text-sm text-white truncate">{file?.name}</span>
            </div>
            <button
              data-testid="new-chat-button"
              onClick={() => { setSessionId(null); setMessages([]); setFile(null); }}
              className="text-xs text-gray-400 hover:text-vault-gold flex items-center gap-1"
            >
              <RefreshCw className="w-3.5 h-3.5" /> New chat
            </button>
          </div>
          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-message-${i}`} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.role === 'user' ? 'bg-vault-gold/20 text-vault-gold' : 'bg-white/5 text-gray-300 border border-white/10'}`}>
                  {m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`max-w-[80%] rounded-2xl p-4 ${m.role === 'user' ? 'bg-vault-gold/10 border border-vault-gold/30' : 'bg-vault-paper/60 border border-white/10'} ${m.error ? 'border-red-500/30' : ''}`}>
                  <div className="prose prose-invert prose-sm prose-p:my-1 prose-strong:text-vault-gold max-w-none text-gray-200">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-gray-300 animate-pulse" />
                </div>
                <div className="bg-vault-paper/60 border border-white/10 rounded-2xl p-4 flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-vault-gold animate-bounce" />
                  <span className="w-2 h-2 rounded-full bg-vault-gold animate-bounce" style={{ animationDelay: '0.15s' }} />
                  <span className="w-2 h-2 rounded-full bg-vault-gold animate-bounce" style={{ animationDelay: '0.3s' }} />
                </div>
              </div>
            )}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="border-t border-white/10 p-4 flex gap-2 items-center"
          >
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about the PDF…"
              disabled={loading}
              className="flex-1 bg-vault-bg border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:border-vault-gold focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={!input.trim() || loading}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
            >
              <Send className="w-4 h-4" /> Send
            </button>
          </form>
        </div>
      )}
      {error && <p className="text-red-400 mt-4">{error}</p>}
    </AIToolShell>
  );
}
